import { useState, useEffect } from 'react'

import Spinner from '~/components/Spinner'

export interface StatsProgressBarProps {
  barType: 'round' | 'straight'
  barHeight?: 'slim' | 'regular' | 'tall'
  progressPercentage: number
  FirstSubtitle?: string
  SecondSubtitle?: string
}

export default function StatsProgressBar({
  progressPercentage,
  FirstSubtitle,
  barHeight,
  SecondSubtitle,
  barType,
}: StatsProgressBarProps): React.ReactElement {
  const [animatedValue, setAnimatedValue] = useState(0)
  const isRound = barType === 'round'

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValue(progressPercentage)
    }, 100)
    return () => clearTimeout(timer)
  }, [progressPercentage])

  const radius = 40
  const strokeWidth = 10
  const normalizedRadius = radius - strokeWidth / 2
  const circumference = normalizedRadius * 2 * Math.PI
  const strokeDashoffset = circumference - (animatedValue / 100) * circumference
  const barHeightValue = barHeight === 'slim' ? 'h-1' : barHeight === 'tall' ? 'h-3' : 'h-2'

  return isRound ? (
    <div className="flex w-full items-center justify-center">
      <div className="relative mx-auto my-3 flex items-center justify-center h-24 w-24">
        <svg className="h-full w-full -rotate-90" viewBox={`0 0 ${radius * 2} ${radius * 2}`}>
          <circle
            className="text-black"
            cx={radius}
            cy={radius}
            fill="transparent"
            r={normalizedRadius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
          />
          <circle
            className="text-green-lime transition-all duration-1000 ease-out"
            cx={radius}
            cy={radius}
            fill="transparent"
            r={normalizedRadius}
            stroke="currentColor"
            strokeDasharray={`${circumference} ${circumference}`}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
            style={{ strokeDashoffset }}
          />
        </svg>

        <div className="absolute text-center">
          {progressPercentage === undefined ? (
            <Spinner size="small" />
          ) : (
            <span className="text-6 font-bold text-white">{Math.floor(progressPercentage ?? 0)}%</span>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex w-full flex-col items-end justify-center gap-2">
      <div className={`relative ${barHeightValue} w-full rounded-full bg-black overflow-hidden`}>
        <div
          className="absolute top-0 left-0 h-full rounded-full bg-green-lime transition-all duration-1000 ease-out"
          style={{ width: `${animatedValue}%` }}
        />
      </div>

      <div className={`flex w-full ${FirstSubtitle && SecondSubtitle ? 'justify-between' : 'justify-end'}`}>
        {FirstSubtitle && <p className="text-xs text-blue-pastel">{FirstSubtitle}</p>}
        {SecondSubtitle && <p className="text-xs text-orange">{SecondSubtitle}</p>}
      </div>
    </div>
  )
}
